import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown } from "lucide-react";
import { MonthlyData } from "@/hooks/use-dashboard-analytics";
import { useValuesVisibility } from "@/hooks/use-values-visibility";

interface ExpenseTrendChartProps {
  data: MonthlyData[];
  isLoading?: boolean;
  compact?: boolean;
}

const chartConfig = {
  income: {
    label: "Receitas",
    color: "hsl(var(--success))",
  },
  expense: {
    label: "Despesas",
    color: "hsl(var(--destructive))",
  },
  balance: {
    label: "Saldo",
    color: "hsl(var(--primary))",
  },
};

export function ExpenseTrendChart({ data, isLoading, compact }: ExpenseTrendChartProps) {
  const chartData = compact ? data.slice(-6) : data;
  const { visible, mask } = useValuesVisibility();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Evolução de Receitas e Gastos</CardTitle>
          <CardDescription>Receitas, despesas e saldo</CardDescription>
        </CardHeader>
        <CardContent className="h-[200px] flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Carregando...</div>
        </CardContent>
      </Card>
    );
  }

  // Calculate balance trend
  const hasData = chartData.length >= 2;
  const firstHalfAvg = hasData
    ? chartData.slice(0, Math.floor(chartData.length / 2)).reduce((s, d) => s + d.balance, 0) / Math.floor(chartData.length / 2)
    : 0;
  const secondHalfAvg = hasData
    ? chartData.slice(Math.floor(chartData.length / 2)).reduce((s, d) => s + d.balance, 0) / Math.ceil(chartData.length / 2)
    : 0;
  const trendPercentage = firstHalfAvg !== 0 
    ? ((secondHalfAvg - firstHalfAvg) / Math.abs(firstHalfAvg)) * 100 
    : 0;
  const isIncreasing = trendPercentage > 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Evolução de Receitas e Gastos</CardTitle>
            <CardDescription>Receitas, despesas e saldo</CardDescription>
          </div>
          {hasData && Math.abs(trendPercentage) > 5 && (
            <div className={`flex items-center gap-1 text-sm ${isIncreasing ? "text-success" : "text-destructive"}`}>
              {isIncreasing ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )}
              <span className="font-medium">
                {Math.abs(trendPercentage).toFixed(0)}%
              </span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className={compact ? "h-[160px] w-full" : "h-[220px] w-full"}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
              <XAxis 
                dataKey="monthLabel" 
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                className="fill-muted-foreground"
              />
              <YAxis 
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                className="fill-muted-foreground"
                tickFormatter={(value) => visible ? `${(value / 1000).toFixed(0)}k` : "***"}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value) =>
                      mask(new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(Number(value)))
                    }
                  />
                }
              />
              {!compact && <Legend
                verticalAlign="top"
                height={30}
                formatter={(value: string) => {
                  const labels: Record<string, string> = { income: "Receitas", expense: "Despesas", balance: "Saldo" };
                  return labels[value] || value;
                }}
              />}
              <Area
                type="monotone"
                dataKey="income"
                name="income"
                stroke="hsl(var(--success))"
                strokeWidth={2}
                fill="url(#incomeGradient)"
              />
              <Area
                type="monotone"
                dataKey="expense"
                name="expense"
                stroke="hsl(var(--destructive))"
                strokeWidth={2}
                fill="url(#expenseGradient)"
              />
              <Area
                type="monotone"
                dataKey="balance"
                name="balance"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                strokeDasharray="5 5"
                fill="url(#balanceGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
